import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import Table from 'react-bootstrap/Table';
import { Alert } from 'react-bootstrap';
import { Query } from '@apollo/client/react/components';
import { QueryResult, useApolloClient } from '@apollo/client';
import './styles.scss';
import {
  STORES_GROUPS_HEADERS,
  STORES_GROUPS_TYPES_MAP,
  ITEMS_GROUPS_HEADERS,
  ITEMS_GROUPS_TYPES_MAP
} from './headers';
import {
  FETCH_GROUPS_LIST,
  STORES_GROUPS_DETAIL_SERIES,
  ITEMS_GROUPS_DETAIL_SERIES
} from 'apollo/queries';
import {
  STORES_GROUPS_TYPE,
  ITEMS_GROUPS_TYPE,
  CONTEXT_STORE_GROUP,
  CONTEXT_ITEM_GROUP,
  HeaderType,
  SortDirection
} from 'utils/types';
import { getData, isEmpty, applyStyle, formatTo } from 'utils/functions';
import { favGroupsClient } from 'apollo';
import LoadingSpinner from 'components/LoadingSpinner';
import _ from 'lodash';
import TableHeaders from 'components/TableHeaders';
import {
  clientIdSelector,
  currentBrandSelector,
  isEnterpriseContextSelector
} from 'store/global/selectors';

export const ROW_STATUS = Object.freeze({
  LOADING: 'loading',
  ERROR: 'error',
  OK: 'ok'
});

const GroupTable: ({
  onRowClick,
  type,
  search,
  clientId,
  globalFilter,
  onQueryFinish,
  currentBrand,
  isEnterpriseContext
}: {
  onRowClick?: any;
  type: any;
  search: any;
  clientId: string;
  globalFilter: any;
  onQueryFinish: any;
  currentBrand: string;
  isEnterpriseContext: boolean;
}) => any = ({
  onRowClick,
  type,
  search,
  clientId,
  globalFilter,
  onQueryFinish,
  currentBrand,
  isEnterpriseContext
}) => {
  let rowHeaders: Array<HeaderType>;
  let rowTypes: Map<string, string>;
  let query: any;
  let context: string;
  if (type === STORES_GROUPS_TYPE) {
    rowHeaders = STORES_GROUPS_HEADERS;
    rowTypes = STORES_GROUPS_TYPES_MAP;
    query = STORES_GROUPS_DETAIL_SERIES;
    context = CONTEXT_STORE_GROUP;
  }
  if (type === ITEMS_GROUPS_TYPE) {
    rowHeaders = ITEMS_GROUPS_HEADERS;
    rowTypes = ITEMS_GROUPS_TYPES_MAP;
    query = ITEMS_GROUPS_DETAIL_SERIES;
    context = CONTEXT_ITEM_GROUP;
  }

  const client = useApolloClient();
  const [rows, setRows] = useState([]);
  const [groupData, setGroupData] = useState({});
  const [currentSortDirection, setCurrentSortDirection] = useState(SortDirection.DESC);
  const defaultHeader: HeaderType = rowHeaders.find(header => header.default);
  const [currentSortedBy, setCurrentSortedBy] = useState(
    defaultHeader ? defaultHeader.sortBy : null
  );

  useEffect(() => {
    queryGroups(rows, groupData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilter]);

  const renderRows = () => {
    const mapTds = row => {
      const tds = [];
      switch (row.status) {
        case ROW_STATUS.LOADING:
          tds.push(
            <td key={row.id} colSpan={rowTypes.size}>
              <LoadingSpinner size="sm" />
            </td>
          );
          break;
        case ROW_STATUS.OK:
          rowTypes.forEach((type: string, attribute: string) => {
            tds.push(
              <td key={`${type}-${attribute}`} style={applyStyle(attribute, row[attribute])}>
                {formatTo(type, row[attribute])}
              </td>
            );
          });
          break;
      }
      return tds;
    };
    // First goes 'ok' row (sorted by current sort attribute and direction), then then loading rows
    // Filter also by search text
    // SIG-788: A row with error should not be displayed
    const sortedRows = _.orderBy(
      rows.filter(
        row =>
          row.status === ROW_STATUS.OK && row.name.toLowerCase().indexOf(search.toLowerCase()) > -1
      ),
      [currentSortedBy],
      [currentSortDirection.toLowerCase()]
    ).concat(rows.filter(row => row.status === ROW_STATUS.LOADING));
    return sortedRows.map(row => {
      return (
        <tr
          className={row.onClick && row.status === ROW_STATUS.OK ? 'clickable' : null}
          onClick={row.onClick && row.status === ROW_STATUS.OK ? row.onClick : null}
          key={row.id}
        >
          <td>{row.name}</td>
          {mapTds(row)}
        </tr>
      );
    });
  };

  const queryGroups = (rows, groupData) => {
    rows.map(row => {
      // For each row query for details.
      return client
        .query({
          query: query,
          variables: {
            clientID: clientId,
            globalFilter: { ...globalFilter, ...groupData[row.name] }
          }
        })
        .then(({ data }) => {
          const newRow = { ...row };
          rowTypes.forEach((type: string, attribute: string) => {
            newRow[attribute] = getData(data)[attribute];
          });
          newRow.status = ROW_STATUS.OK;
          setRows(prevRows =>
            prevRows.map(prevRow => {
              if (prevRow.id === newRow.id) {
                return newRow;
              }
              return prevRow;
            })
          );
        })
        .catch(() => {
          const errorRow = { ...row };
          errorRow.status = ROW_STATUS.ERROR;
          setRows(prevRows =>
            prevRows.map(prevRow => {
              if (prevRow.id === errorRow.id) {
                return errorRow;
              }
              return prevRow;
            })
          );
        });
    });
  };

  return (
    <Query
      query={FETCH_GROUPS_LIST}
      client={favGroupsClient as any}
      variables={{
        clientID: clientId,
        context,
        ...(isEnterpriseContext && { brand: currentBrand })
      }}
      onCompleted={data => {
        onQueryFinish(getData(data).length);
        const filteredData = getData(data).filter(elem => elem.context === context);
        const rows = [];
        const groupData = {};
        filteredData.forEach(rowData => {
          const row = {
            id: rowData.id,
            name: rowData.name,
            status: ROW_STATUS.LOADING // empty rows should appear as loading
          };
          rowTypes.forEach((type: string, attribute: string) => {
            row[attribute] = undefined;
          });
          if (onRowClick) {
            row['onClick'] = () => onRowClick(rowData.name, { data: rowData.data }, rowData.id);
          }
          rows.push(row);
          groupData[rowData.name] = rowData.data;
        });
        setRows(rows);
        setGroupData(groupData);
        queryGroups(rows, groupData);
      }}
      onError={() => {
        onQueryFinish('?');
      }}
    >
      {(result: QueryResult) => {
        const { loading, error, data } = result;
        if (loading) return <LoadingSpinner />;
        if (error) return <Alert variant="danger">{error.message}</Alert>;
        if (isEmpty(data)) return <Alert variant="warning">No Data Found</Alert>;
        return (
          <React.Fragment>
            <Table className="group-table" responsive>
              <colgroup>
                <col />
                <col />
              </colgroup>
              <TableHeaders
                currentSortedBy={currentSortedBy}
                currentSortDirection={currentSortDirection}
                headers={rowHeaders}
                onSort={(sortBy, sortDirection) => {
                  setCurrentSortedBy(sortBy);
                  setCurrentSortDirection(sortDirection);
                }}
              />
              <tbody>{renderRows()}</tbody>
            </Table>
          </React.Fragment>
        );
      }}
    </Query>
  );
};

const mapStateToProps = state => ({
  clientId: clientIdSelector(state),
  currentBrand: currentBrandSelector(state),
  isEnterpriseContext: isEnterpriseContextSelector(state)
});

export default connect(mapStateToProps)(GroupTable);
